import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import ListActions from 'Components/ListActions'
import PageTitle from 'Components/PageTitle'
import SearchByField from 'Components/SearchByField'
import { AffiliateOrderFilters } from 'Services/SalesManagement/Features/Order/Components/AffiliateOrderFilters'
import { AffiliateOrderList } from 'Services/SalesManagement/Features/Order/Components/AffiliateOrderList'
import { useGetMobileOrderFilteredSearchOptionsQuery } from 'Services/SalesManagement/Features/Order/orderApi'
import {
  fetchMobileOrderList,
  type MobileOrderParamShape
} from 'Services/SalesManagement/Features/Order/orderSlice'
import useSalesManagementBreadcrumbs from 'Services/SalesManagement/Hooks/useSalesManagementBreadcrumbs'
import useTypedSelector, { useAppDispatch } from 'Store'
import { getFilterByDefaultDate, SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { dateFormatter } from 'Util/formatters'
import useQuery from 'Util/getQueryParamsHook'
import type { SelectChangeEvent } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

export default function MobileOrderListPage() {
  const dispatch = useAppDispatch()
  const queryParams = useQuery()
  const navigate = useNavigate()

  const { mobileOrders, count, loading } = useTypedSelector(state => state.order)

  const { data: mobileOrderFilterChoices, isFetching: isMobileOrderFilterOptionsFetching } =
    useGetMobileOrderFilteredSearchOptionsQuery()

  const rows = mobileOrders

  const [searchText, setSearchText] = useState(queryParams.get('search') ?? '')
  const [filters, setFilters] = useState<Partial<MobileOrderParamShape>>({
    operator_id: queryParams.get('operator_id') ?? '',
    sales_channel_name: queryParams.get('sales_channel_name') ?? '',
    payment_method: queryParams.get('payment_method') ?? '',
    refund_status: queryParams.get('refund_status') ?? '',
    created_after: queryParams.get('created_after') ?? dateFormatter(getFilterByDefaultDate()),
    created_before: queryParams.get('created_before') ?? ''
  })
  const defaultSearchFieldChoice = mobileOrderFilterChoices?.search_field_choices[0][0] ?? ''
  const initialSearchFieldChoice = queryParams.get('search_field') ?? defaultSearchFieldChoice
  const [searchField, setSearchField] = useState(initialSearchFieldChoice)

  const [params, setParams] = useState<MobileOrderParamShape>({
    ordering: queryParams.get('ordering') ?? '-created',
    search: queryParams.get('search') ?? '',
    search_field: initialSearchFieldChoice,
    page: queryParams.get('page') ? Number.parseInt(queryParams.get('page') as string, 10) : 1,
    operator_id: queryParams.get('operator_id') ?? '',
    sales_channel_name: queryParams.get('sales_channel_name') ?? '',
    payment_method: queryParams.get('payment_method') ?? '',
    refund_status: queryParams.get('refund_status') ?? '',
    created_after: queryParams.get('created_after') ?? dateFormatter(getFilterByDefaultDate()),
    created_before: queryParams.get('created_before') ?? ''
  })

  useEffect(() => {
    setSearchField(initialSearchFieldChoice)
  }, [mobileOrderFilterChoices, initialSearchFieldChoice])

  // biome-ignore lint/correctness/useExhaustiveDependencies: setQueryParams changes on every re-render and should not be used as a hook dependency.
  useEffect(() => {
    dispatch(fetchMobileOrderList(params))
      .then(unwrapResult)
      .then(() => {
        setQueryParams()
      })
  }, [dispatch, params])

  const setQueryParams = () => {
    const query: string[] = []
    Object.keys(params).forEach((key: string) => {
      const value = params[key as keyof MobileOrderParamShape]
      if (value) {
        query.push(`${key}=${value}`)
      }
    })
    const queryString = query.join('&')

    navigate({
      pathname: location.pathname,
      search: `?${queryString}`
    })
  }

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = params.ordering.includes('-')
    setParams(prevState => ({
      ...prevState,
      ordering: `${isAsc ? '' : '-'}${property}`
    }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({ ...prevState, page: newPage + 1 }))
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleSearchFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setSearchField(value)
  }

  const handleSelectChange = (event: SelectChangeEvent, name: keyof MobileOrderParamShape) => {
    const { value } = event.target
    const newValue = params[name] === value ? '' : value
    setFilters(prevState => ({ ...prevState, [name]: newValue }))
  }

  const handleDatePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = dateFormatter(value).toString()
    }
    setFilters(prevState => ({ ...prevState, [name]: formattedValue }))
  }

  const submitFilteredSearch = () => {
    setParams(prevState => ({
      ...prevState,
      ...filters,
      search_field: searchField,
      search: searchText,
      page: 1
    }))
  }

  const clearFilters = () => {
    setFilters(prevState => ({
      ...prevState,
      operator_id: '',
      sales_channel_name: '',
      payment_method: '',
      refund_status: '',
      created_after: '',
      created_before: ''
    }))
  }

  const breadcrumbs = useSalesManagementBreadcrumbs([
    {
      title: t`Orders`
    },
    {
      title: t`Mobile orders`
    }
  ])

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <BackButton link={SALES_MANAGEMENT_ROOT_URL} />
        <PageTitle>{t`Mobile orders`}</PageTitle>
        <Typography component="p" variant="subtitle1" sx={{ fontSize: '15px', mb: 4 }}>
          {t`In the Mobile orders section you can search for, filter and sort the orders made in the Waltti Mobile app. The order listing below is refreshed at regular intervals, so note that the latest orders are not listed here in real-time and the changes made to a single order are not updated in this list in real-time. Also note that orders older than one year are not included in the list.`}
        </Typography>
        <ListActions>
          <SearchByField
            searchText={searchText}
            handleSearchTextChange={handleSearchTextChange}
            handleSearch={submitFilteredSearch}
            searchLabelText={t`Search for mobile orders`}
            searchHelperText={t`Search for external ID, travel account, email address, or Waltti ID`}
            searchField={searchField}
            isSearchFieldChoicesLoading={isMobileOrderFilterOptionsFetching}
            searchFieldChoices={mobileOrderFilterChoices?.search_field_choices ?? []}
            handleSearchFieldChange={handleSearchFieldChange}
          />
        </ListActions>
      </Box>
      <AffiliateOrderFilters
        isFiltersLoading={isMobileOrderFilterOptionsFetching}
        filterChoices={mobileOrderFilterChoices?.field_choices}
        currentFilters={filters}
        clearFilters={clearFilters}
        submitFilters={submitFilteredSearch}
        handleDatePickerChange={handleDatePickerChange}
        handleSelectChange={handleSelectChange}
      />
      <AffiliateOrderList
        rows={rows}
        loading={loading}
        params={params}
        handleRequestSort={handleRequestSort}
        handleChangePage={handleChangePage}
        count={count}
        singleUrl="mobile-orders"
      />
    </Box>
  )
}
